import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Lucide React Icons
import { CheckCircle, Download, Mail, X } from 'lucide-react';

// Card Components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = '' }) => (
  <h2 className={`text-xl font-semibold ${className}`}>{children}</h2>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`${className}`}>{children}</div>
);

// Button Component
const Button = ({ children, onClick, disabled, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`px-6 py-3 rounded-lg font-semibold transition-all ${
      disabled
        ? 'bg-gray-300 cursor-not-allowed'
        : 'bg-blue-600 hover:bg-blue-700 text-white'
    } ${className}`}
  >
    {children}
  </button>
);

const QRGPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const [formData, setFormData] = useState({
    url: '',
    website_name: '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    opt_in: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/qrg/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setQrCodeData(data);
        setShowModal(true);
        toast.success('QR Code generated successfully!');
      } else {
        toast.error(data.error_message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while generating the QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCodeData?.qr_code_image_base64) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrCodeData.qr_code_image_base64}`;
    link.download = `qr-code-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded!');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    setEmailSending(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('email', emailAddress);
      formBody.append('qr_code_base64', qrCodeData.qr_code_image_base64);
      formBody.append('url', qrCodeData.url);
      
      const response = await fetch(`${API}/api/qrg/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });
      
      if (response.ok) {
        toast.success('QR code emailed successfully! Check your inbox.');
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while sending the email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setQrCodeData(null);
    // Reset form for next generation
    setFormData({
      url: '',
      website_name: '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      opt_in: false
    });
    
    // Redirect to originating page
    if (user) {
      navigate('/homepage');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📱 QR Code Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Generate professional, high-resolution QR codes for any website URL in seconds. Perfect for marketing materials, business cards, product packaging, and digital campaigns. Receive instant downloads with optional email delivery. Completely free with unlimited use and no restrictions or hidden fees.
          </p>
        </div>

        {/* Pricing Info */}
        <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-green-900 mb-2">✨ 100% Free Forever</h3>
            <p className="text-green-700">No registration required • Unlimited QR codes • No hidden fees</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* QR Code Generation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200">
              {/* Form Header with Logo */}
              <div className="p-6 border-b border-slate-200">
                <div className="text-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-10 mx-auto mb-3"
                  />
                  <h2 className="text-2xl font-bold text-slate-900">📱 Generate Your QR Code</h2>
                  <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
              {/* URL Input */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Website URL *
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://www.example.com or www.example.com"
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter any website URL (we'll add https:// if needed)
                </p>
              </div>

              {/* Website Name Input */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Website Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData.website_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, website_name: e.target.value }))}
                  placeholder="My Website"
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This name will appear below the QR code to help you identify it
                </p>
              </div>

              {/* Non-logged-in user fields */}
              {!user && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <input
                      type="checkbox"
                      checked={formData.opt_in}
                      onChange={(e) => setFormData(prev => ({ ...prev, opt_in: e.target.checked }))}
                      className="mt-1 w-4 h-4"
                      required
                    />
                    <label className="text-sm text-slate-700">
                      I agree to receive helpful publishing tips and updates from Digital Ink AI. 
                      You can unsubscribe anytime.
                    </label>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⚙️</span>
                    Generating QR Code...
                  </span>
                ) : (
                  '📱 Generate QR Code with Digital Ink AI'
                )}
              </button>
            </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Generation Features */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>High-resolution QR codes (300 DPI)</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Instant download to your device</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Optional email delivery</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Works with any website URL</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Print-ready PNG format</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>No watermarks or branding</span>
                </div>
              </div>
            </div>

            {/* Usage Tips */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">💡 Usage Tips</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-emerald-500 mr-2">•</span>
                  <span><strong>Marketing:</strong> Business cards, flyers, posters</span>
                </div>
                <div className="flex items-start">
                  <span className="text-emerald-500 mr-2">•</span>
                  <span><strong>Product:</strong> Packaging, labels, tags</span>
                </div>
                <div className="flex items-start">
                  <span className="text-emerald-500 mr-2">•</span>
                  <span><strong>Digital:</strong> Email signatures, social media</span>
                </div>
                <div className="flex items-start">
                  <span className="text-emerald-500 mr-2">•</span>
                  <span><strong>Events:</strong> Tickets, badges, signage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* QR Code Result Modal */}
        {showModal && qrCodeData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-t-xl flex justify-between items-center">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  Your QR Code is Ready!
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* QR Code Display */}
                <div className="bg-gray-50 p-8 rounded-lg text-center">
                  <img
                    src={`data:image/png;base64,${qrCodeData.qr_code_image_base64}`}
                    alt="Generated QR Code"
                    className="mx-auto max-w-sm w-full"
                  />
                  <p className="text-sm text-slate-600 mt-4">
                    <strong>URL:</strong> {qrCodeData.url}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    onClick={handleDownload}
                    className="flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download QR Code
                  </Button>

                  <Button
                    onClick={() => document.getElementById('email-form').scrollIntoView({ behavior: 'smooth' })}
                    className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Mail className="w-5 h-5" />
                    Email to Me
                  </Button>
                </div>

                {/* Email Form */}
                <div id="email-form" className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-slate-800 mb-3">📧 Email This QR Code</h3>
                  <form onSubmit={handleEmailSubmit} className="flex gap-2">
                    <input
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-4 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <Button
                      type="submit"
                      disabled={emailSending}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {emailSending ? 'Sending...' : 'Send'}
                    </Button>
                  </form>
                </div>

                {/* Usage Tips */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-slate-800 mb-2">💡 Quick Tips</h3>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• Test with your phone camera before printing</li>
                    <li>• Print at 300 DPI or higher for best quality</li>
                    <li>• Ensure good contrast (black on white works best)</li>
                    <li>• Leave white space around the QR code</li>
                    <li>• Works even if partially damaged (high error correction)</li>
                  </ul>
                </div>

                {/* Close Button */}
                <Button
                  onClick={handleCloseModal}
                  className="w-full bg-slate-600 hover:bg-slate-700"
                >
                  Done - Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default QRGPage;
